import React, { useState, useEffect } from "react";
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, sendPasswordResetEmail, GoogleAuthProvider, signInWithPopup, updateProfile, signInWithPhoneNumber } from "firebase/auth";
import { setDoc, doc, getDoc, updateDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { auth, db, setupRecaptcha, resetRecaptcha, clearRecaptchaVerifier, functions, recaptchaConfigReady } from "./firebase";

function Auth({ onAuthSuccess }) {
  const [isSignIn, setIsSignIn] = useState(true);
  const [email, setEmail] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [name, setName] = useState("");
  const [area, setArea] = useState("");
  const [contact, setContact] = useState("");
  const [carrier, setCarrier] = useState(""); // digicel | bmobile
  const [verifyChannel, setVerifyChannel] = useState("whatsapp");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [lockoutUntil, setLockoutUntil] = useState(null);
  
  // Phone Verification states (SMS or WhatsApp)
  const [showSmsVerification, setShowSmsVerification] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [verificationMethod, setVerificationMethod] = useState("sms"); // sms | whatsapp
  const [smsError, setSmsError] = useState("");
  const [smsLoading, setSmsLoading] = useState(false);
  const [recaptchaVerifier, setRecaptchaVerifier] = useState(null);
  const [tempUserData, setTempUserData] = useState(null);
  const [accountCreated, setAccountCreated] = useState(false);

  const handleCarrierChange = (value) => {
    setCarrier(value);
    setVerifyChannel("whatsapp");
  };

  // Check if user is currently locked out
  const isLockedOut = () => {
    if (!lockoutUntil) return false;
    return new Date() < new Date(lockoutUntil);
  };

  // Get remaining lockout time
  const getRemainingLockoutTime = () => {
    if (!lockoutUntil) return 0;
    const now = new Date();
    const lockoutTime = new Date(lockoutUntil);
    return Math.max(0, Math.ceil((lockoutTime - now) / (1000 * 60))); // minutes
  };

  // Load failed attempts from localStorage on component mount
  useEffect(() => {
    const savedAttempts = localStorage.getItem('auth_failed_attempts');
    const savedLockout = localStorage.getItem('auth_lockout_until');
    
    if (savedAttempts) {
      setFailedAttempts(parseInt(savedAttempts));
    }
    if (savedLockout) {
      setLockoutUntil(savedLockout);
    }
  }, []);

  // Setup reCAPTCHA on component mount (warm-up; send path always refreshes)
  useEffect(() => {
    let cancelled = false;
    let verifierRef = null;

    const initializeRecaptcha = async () => {
      try {
        const existingContainer = document.getElementById('recaptcha-container');
        if (existingContainer) {
          existingContainer.innerHTML = '';
        }
        await new Promise((resolve) => setTimeout(resolve, 100));
        const verifier = await setupRecaptcha('recaptcha-container');
        if (cancelled) {
          clearRecaptchaVerifier(verifier);
          return;
        }
        verifierRef = verifier;
        setRecaptchaVerifier(verifier);
      } catch (error) {
        console.error('Error setting up reCAPTCHA:', error);
      }
    };

    initializeRecaptcha();

    return () => {
      cancelled = true;
      clearRecaptchaVerifier(verifierRef);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      let userCred;
      if (isSignIn) {
        if (!email || !password) {
          setError("Please enter email and password.");
          setLoading(false);
          return;
        }
        
        // Check login attempts with backend BEFORE trying to sign in
        const checkAttempts = httpsCallable(functions, 'checkLoginAttempts');
        const attemptCheck = await checkAttempts({ email: email });
        console.log("Login attempt check:", attemptCheck.data);
        
        if (!attemptCheck.data.allowed) {
          setError(attemptCheck.data.message);
          setLoading(false);
          return;
        }
        
        // Attempt sign in
        userCred = await signInWithEmailAndPassword(auth, email, password);
        
        // Reset failed attempts on successful login (backend)
        const resetAttempts = httpsCallable(functions, 'resetLoginAttempts');
        await resetAttempts({ email: email });
        console.log("✅ Login successful, attempts reset");
        
        // Clear local storage (legacy cleanup)
        setFailedAttempts(0);
        setLockoutUntil(null);
        localStorage.removeItem('auth_failed_attempts');
        localStorage.removeItem('auth_lockout_until');
      } else {
        // Validate all fields
        if (!name || !area || !contact || !email || !confirmEmail || !password || !confirmPassword) {
          setError("Please fill in all fields.");
          setLoading(false);
          return;
        }

        if (!carrier) {
          setError("Please select Digicel or Bmobile.");
          setLoading(false);
          return;
        }

        const channel = "whatsapp";
        
        // Check if emails match
        if (email !== confirmEmail) {
          setError("Emails do not match.");
          setLoading(false);
          return;
        }
        
        // Validate contact number (must be exactly 7 digits)
        if (!/^\d{7}$/.test(contact)) {
          setError("Contact number must be exactly 7 digits.");
          setLoading(false);
          return;
        }
        
        // Check if passwords match
        if (password !== confirmPassword) {
          setError("Passwords do not match.");
          setLoading(false);
          return;
        }
        
        // Check if phone number is in 7-day cooldown period BEFORE sending code
        const fullPhoneNumber = `+1868${contact}`;
        console.log("Checking phone number cooldown for:", fullPhoneNumber);
        try {
          const checkCooldown = httpsCallable(functions, 'checkPhoneNumberCooldown');
          const cooldownResult = await checkCooldown({ phoneNumber: fullPhoneNumber });
          console.log("Cooldown check result:", cooldownResult.data);
          
          if (!cooldownResult.data.available) {
            const errorMsg = cooldownResult.data.message || "This phone number was recently verified. Please try again later.";
            console.error("❌ Phone number in cooldown!");
            setError(errorMsg);
            setLoading(false);
            return;
          }
          console.log("✅ Phone number is available (no cooldown)");
        } catch (cooldownError) {
          console.error("Error checking cooldown:", cooldownError);
          // Show error but don't block if it's just a network issue
          if (cooldownError.message && cooldownError.message.includes('blocked')) {
            setError(cooldownError.message);
            setLoading(false);
            return;
          }
        }
        
        console.log(`Starting ${channel} verification for:`, contact);
        try {
          await sendWhatsAppVerificationCode();
        } catch (err) {
          console.error("Error sending verification code:", err);
          setError("Please contact us via call or WhatsApp on 481 - 1471");
        }
        setLoading(false);
        return;
      }
      setLoading(false);
      onAuthSuccess(userCred.user);
    } catch (err) {
      setLoading(false);
      
      // Handle failed login attempts for sign-in only (record in backend)
      if (isSignIn && (err.code === 'auth/wrong-password' || err.code === 'auth/user-not-found' || err.code === 'auth/invalid-email' || err.code === 'auth/invalid-credential')) {
        try {
          // Record failed attempt in backend
          const recordFailed = httpsCallable(functions, 'recordFailedLogin');
          const result = await recordFailed({ email: email });
          console.log("Failed login recorded:", result.data);
          
          // Display the error message from backend
          setError(result.data.message);
          
          // Update local state for UI (but backend is source of truth)
          setFailedAttempts(result.data.failedAttempts);
          if (result.data.lockedOut) {
            setLockoutUntil(new Date(Date.now() + 60 * 60 * 1000).toISOString());
          }
        } catch (recordError) {
          console.error("Error recording failed login:", recordError);
          // Fallback error message
          setError('Wrong username or password.');
        }
      } else {
        setError(err.message.replace('Firebase: ', ''));
      }
    }
  };

  const handlePasswordReset = async () => {
    setError("");
    setResetSent(false);
    if (!email) {
      setError("Please enter your email to reset password.");
      return;
    }
    
    setLoading(true); // Prevent multiple clicks
    
    try {
      // Record the password reset FIRST (this checks eligibility and records in one atomic operation)
      const recordReset = httpsCallable(functions, 'recordPasswordReset');
      const recordResult = await recordReset({ email: email });
      console.log("Password reset record result:", recordResult.data);
      
      if (!recordResult.data.success) {
        setError(recordResult.data.message);
        setLoading(false);
        return;
      }
      
      // Now send the email (only if recording succeeded)
      const actionCodeSettings = {
        url: 'https://rocketexpresstt.com/',
        handleCodeInApp: false
      };
      
      await sendPasswordResetEmail(auth, email, actionCodeSettings);
      
      console.log("✅ Password reset email sent to:", email);
      setResetSent(true);
      setLoading(false);
    } catch (err) {
      console.error("❌ Password reset error:", err);
      setError(err.message.replace('Firebase: ', ''));
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError("");
    setLoading(true);
    try {
      const provider = new GoogleAuthProvider();
      // Always show account selection dialog
      provider.setCustomParameters({
        prompt: 'select_account'
      });
      const result = await signInWithPopup(auth, provider);
      
      // Check if Firestore user doc exists
      const userDocRef = doc(db, "users", result.user.uid);
      const userDocSnap = await getDoc(userDocRef);
      
      if (!userDocSnap.exists()) {
        // New Google user - create basic document
        const membershipNumber = result.user.uid.substring(0, 8).toUpperCase();
        await setDoc(userDocRef, { 
          email: result.user.email,
          name: result.user.displayName || "",
          area: "", 
          contact: "", 
          membershipNumber
        });
      }
      // For existing Google users, no need to update anything - they can complete profile later from account page
      
      setLoading(false);
      onAuthSuccess(result.user);
    } catch (err) {
      setLoading(false);
      setError(err.message.replace('Firebase: ', ''));
    }
  };

  const sendVerificationCode = async () => {
    setSmsError("");
    setError(""); // Clear main error too
    setSmsLoading(true);
    console.log("sendVerificationCode called");
    
    try {
      const fullPhoneNumber = `+1868${contact}`;
      console.log("Full phone number:", fullPhoneNumber);
      
      // First, check if email is already registered
      console.log("Checking email availability for:", email);
      const checkEmail = httpsCallable(functions, 'checkEmailAvailability');
      const emailResult = await checkEmail({ email: email });
      console.log("Email check result:", emailResult.data);
      
      if (!emailResult.data.available) {
        const errorMsg = "This email is already registered. Please sign in or use a different email.";
        console.error("❌ Email already in use!");
        setSmsError(errorMsg);
        setError(errorMsg);
        setSmsLoading(false);
        throw new Error(errorMsg);
      }
      console.log("✅ Email is available");
      
      // Note: Cooldown check is done earlier in handleSubmit (before "Create Account" is clicked)
      // This ensures users get feedback immediately without waiting for SMS

      console.log("Sending SMS to", fullPhoneNumber);

      await recaptchaConfigReady;

      // Always use a fresh reCAPTCHA widget for this attempt (reuse causes auth/internal-error)
      let verifier;
      try {
        verifier = await resetRecaptcha(recaptchaVerifier, 'recaptcha-container');
        setRecaptchaVerifier(verifier);
      } catch (setupErr) {
        console.error('Error preparing reCAPTCHA:', setupErr);
        const errorMsg =
          'Security check failed to load. Refresh the page and try again.';
        setSmsError(errorMsg);
        setError(errorMsg);
        setSmsLoading(false);
        return;
      }

      const confirmation = await signInWithPhoneNumber(
        auth,
        fullPhoneNumber,
        verifier
      );
      setConfirmationResult(confirmation);
      
      // Store temporary user data for account creation after verification
      setTempUserData({ name, area, contact, email, password, carrier, verifyChannel: "sms" });
      setVerificationMethod("sms");
      
      setShowSmsVerification(true);
      setSmsLoading(false);
    } catch (err) {
      console.error("SMS Error:", err);
      console.error("SMS Error details:", {
        code: err.code,
        message: err.message,
        customData: err.customData,
      });
      setSmsLoading(false);
      
      let errorMessage = err.message || "Failed to send verification code.";
      if (err.code === 'auth/invalid-phone-number') {
        errorMessage = "Invalid phone number format.";
      } else if (err.code === 'auth/too-many-requests') {
        errorMessage = "Too many attempts. Please try again later.";
      } else if (err.code === 'auth/error-code:-39' || err.message?.includes('error-code:-39')) {
        errorMessage =
          "SMS could not be sent — Firebase's SMS service returned an error (often carrier or temporary). " +
          "Your number and reCAPTCHA are fine. Wait before trying again, use Wi‑Fi, or try another network/number. " +
          "Retrying many times can make it worse.";
      } else if (err.code === 'auth/internal-error') {
        errorMessage =
          "Phone verification failed (Firebase/reCAPTCHA temporary error). " +
          "Refresh the page and try once more. If it keeps failing, wait a few minutes, " +
          "try another network, or confirm Phone sign-in is enabled and Auth reCAPTCHA is Off in Firebase Console.";
      } else if (err.message) {
        errorMessage = err.message.replace('Firebase: ', '');
      }
      
      setSmsError(errorMessage);
      setError(errorMessage); // Also set main error
      
      // Soft-reset reCAPTCHA for next attempt (never throw out of this catch)
      try {
        const fresh = await resetRecaptcha(recaptchaVerifier, 'recaptcha-container');
        setRecaptchaVerifier(fresh);
      } catch (error) {
        console.warn('Error resetting reCAPTCHA after SMS failure:', error);
        setRecaptchaVerifier(null);
      }
    }
  };

  const sendWhatsAppVerificationCode = async () => {
    setSmsError("");
    setError("");
    setSmsLoading(true);
    console.log("sendWhatsAppVerificationCode called");

    try {
      const fullPhoneNumber = `+1868${contact}`;
      console.log("Sending WhatsApp code to", fullPhoneNumber);

      const checkEmail = httpsCallable(functions, "checkEmailAvailability");
      const emailResult = await checkEmail({ email });
      if (!emailResult.data.available) {
        const errorMsg =
          "This email is already registered. Please sign in or use a different email.";
        setSmsError(errorMsg);
        setError(errorMsg);
        setSmsLoading(false);
        throw new Error(errorMsg);
      }

      const sendWa = httpsCallable(functions, "sendWhatsAppVerificationCode");
      const result = await sendWa({
        phoneNumber: fullPhoneNumber,
        email: email.trim().toLowerCase(),
      });

      if (!result.data?.success) {
        const errorMsg =
          carrier === "digicel"
            ? "Please contact us via call or WhatsApp on 481 - 1471"
            : result.data?.message ||
              "Failed to send WhatsApp verification code.";
        setSmsError(errorMsg);
        setError(errorMsg);
        setSmsLoading(false);
        throw new Error(errorMsg);
      }

      setConfirmationResult(null);
      setTempUserData({
        name,
        area,
        contact,
        email,
        password,
        carrier,
        verifyChannel: "whatsapp",
      });
      setVerificationMethod("whatsapp");
      setShowSmsVerification(true);
      setSmsLoading(false);
    } catch (err) {
      console.error("WhatsApp send error:", err);
      setSmsLoading(false);
      const errorMessage =
        carrier === "digicel"
          ? "Please contact us via call or WhatsApp on 481 - 1471"
          : err.message?.replace("Firebase: ", "") ||
            "Failed to send WhatsApp verification code.";
      setSmsError(errorMessage);
      setError(errorMessage);
      throw err;
    }
  };

  const finishAccountAfterPhoneVerified = async () => {
    const userCred = await createUserWithEmailAndPassword(
      auth,
      tempUserData.email,
      tempUserData.password
    );
    await updateProfile(userCred.user, { displayName: tempUserData.name });

    const membershipNumber = userCred.user.uid.substring(0, 8).toUpperCase();
    const fullContactNumber = `+1868${tempUserData.contact}`;

    console.log("📞 Claiming phone number from other users...");
    try {
      const claimPhone = httpsCallable(functions, "claimPhoneNumber");
      const claimResult = await claimPhone({ phoneNumber: fullContactNumber });
      console.log(`✅ ${claimResult.data.message}`);
    } catch (claimError) {
      console.warn("⚠️ Could not claim phone number:", claimError);
    }

    await setDoc(doc(db, "users", userCred.user.uid), {
      email: tempUserData.email,
      name: tempUserData.name,
      area: tempUserData.area,
      contact: fullContactNumber,
      membershipNumber,
      profileCompleted: true,
      phoneVerified: true,
      phoneVerifiedAt: new Date().toISOString(),
      phoneCarrier: tempUserData.carrier || null,
      phoneVerifyChannel: tempUserData.verifyChannel || verificationMethod,
    });

    console.log("✅ User document created successfully with all fields");
    await new Promise((resolve) => setTimeout(resolve, 500));
    return userCred;
  };

  const verifyCode = async () => {
    setSmsError("");
    setSmsLoading(true);
    
    try {
      if (!verificationCode || verificationCode.length !== 6) {
        setSmsError("Please enter a valid 6-digit code.");
        setSmsLoading(false);
        return;
      }

      if (verificationMethod === "whatsapp") {
        const fullPhoneNumber = `+1868${tempUserData.contact}`;
        const verifyWa = httpsCallable(functions, "verifyWhatsAppVerificationCode");
        const result = await verifyWa({
          phoneNumber: fullPhoneNumber,
          email: tempUserData.email.trim().toLowerCase(),
          code: verificationCode,
        });
        if (!result.data?.success) {
          setSmsError(result.data?.message || "Invalid verification code.");
          setSmsLoading(false);
          return;
        }
      } else {
        if (!confirmationResult) {
          setSmsError("No verification in progress. Please try again.");
          setSmsLoading(false);
          return;
        }
        await confirmationResult.confirm(verificationCode);
      }

      setAccountCreated(true);
      const userCred = await finishAccountAfterPhoneVerified();
      onAuthSuccess(userCred.user);
    } catch (err) {
      console.error("Verification Error:", err);
      setAccountCreated(false);
      setSmsLoading(false);
      
      if (err.code === 'auth/invalid-verification-code') {
        setSmsError("Invalid verification code. Please try again.");
      } else if (err.code === 'auth/code-expired') {
        setSmsError("Verification code expired. Please request a new code.");
      } else if (err.code === 'auth/email-already-in-use') {
        setSmsError("Email already in use. Please sign in instead.");
      } else {
        setSmsError((err.message || "Verification failed").replace('Firebase: ', ''));
      }
    }
  };

  const resendVerificationCode = async () => {
    setVerificationCode("");
    setSmsError("");
    setShowSmsVerification(false);
    setConfirmationResult(null);
    if (verificationMethod === "whatsapp") {
      await sendWhatsAppVerificationCode();
    } else {
      await sendVerificationCode();
    }
  };

  const cancelSmsVerification = async () => {
    const channelLabel =
      verificationMethod === "whatsapp" ? "WhatsApp" : "text message";
    const confirmed = window.confirm(
      `Are you sure you want to cancel the ${channelLabel} verification process?\n\n` +
      "If on website, refresh page after cancelling."
    );
    
    if (confirmed) {
      setShowSmsVerification(false);
      setVerificationCode("");
      setConfirmationResult(null);
      setSmsError("");
      setTempUserData(null);
      setVerificationMethod("sms");
      
      // Reset reCAPTCHA
      try {
        const fresh = await resetRecaptcha(recaptchaVerifier, 'recaptcha-container');
        setRecaptchaVerifier(fresh);
      } catch (error) {
        console.warn('Error resetting reCAPTCHA:', error);
        setRecaptchaVerifier(null);
      }
    }
  };

  if (accountCreated) {
    return (
      <div style={{ minHeight: '100vh', background: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ background: '#fff', borderRadius: '12px', boxShadow: '0 4px 16px rgba(0,0,0,0.07)', padding: '2.5rem 2rem', minWidth: 320, maxWidth: 420, textAlign: 'center' }}>
          <h2 style={{ color: '#f97316', fontWeight: 'bold', marginBottom: '1rem' }}>
            Account created
          </h2>
          <p style={{ color: '#374151', margin: 0, lineHeight: 1.5 }}>
            Taking you to the wash page. Please wait.
          </p>
        </div>
      </div>
    );
  }

  // SMS Verification Modal
  if (showSmsVerification) {
    return (
      <div style={{ minHeight: '100vh', background: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ background: '#fff', borderRadius: '12px', boxShadow: '0 4px 16px rgba(0,0,0,0.07)', padding: '2.5rem 2rem', minWidth: 320, maxWidth: 400 }}>
          <h2 style={{ color: '#f97316', fontWeight: 'bold', textAlign: 'center', marginBottom: '1rem' }}>
            Verify Phone Number
          </h2>
          <p style={{ textAlign: 'center', color: '#6b7280', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
            {verificationMethod === "whatsapp"
              ? "We've sent a 6-digit verification code via WhatsApp to"
              : "We've sent a 6-digit verification code to"}
            <br />
            <strong>+1868{contact || tempUserData?.contact}</strong>
            {verificationMethod === "whatsapp" && (
              <>
                <br />
                <span style={{ fontSize: "0.85rem" }}>Check your WhatsApp messages</span>
              </>
            )}
          </p>
          
          <input
            type="text"
            placeholder="Enter 6-digit code"
            value={verificationCode}
            onChange={e => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            style={{ 
              width: '100%', 
              padding: '0.75rem', 
              marginBottom: '1rem', 
              borderRadius: 6, 
              border: '1px solid #e5e7eb', 
              boxSizing: 'border-box',
              fontSize: '1.2rem',
              textAlign: 'center',
              letterSpacing: '0.5rem'
            }}
            autoFocus
            maxLength={6}
          />
          
          {smsError && <div style={{ color: 'red', marginBottom: '1rem', textAlign: 'center', fontSize: '0.9rem' }}>{smsError}</div>}
          
          <button 
            onClick={verifyCode}
            disabled={smsLoading || verificationCode.length !== 6}
            style={{ 
              width: '100%', 
              background: (smsLoading || verificationCode.length !== 6) ? '#9ca3af' : '#f97316', 
              color: '#fff', 
              border: 'none', 
              borderRadius: 6, 
              padding: '0.75rem', 
              fontWeight: 'bold', 
              fontSize: '1rem', 
              cursor: (smsLoading || verificationCode.length !== 6) ? 'not-allowed' : 'pointer', 
              marginBottom: '0.75rem' 
            }}
          >
            {smsLoading ? 'Checking code...' : 'Verify Code'}
          </button>
          
          <button 
            onClick={resendVerificationCode}
            disabled={smsLoading}
            style={{ 
              width: '100%', 
              background: '#fff', 
              color: '#f97316', 
              border: '1px solid #f97316', 
              borderRadius: 6, 
              padding: '0.75rem', 
              fontWeight: 'bold', 
              fontSize: '1rem', 
              cursor: smsLoading ? 'not-allowed' : 'pointer', 
              marginBottom: '0.75rem' 
            }}
          >
            Resend Code
          </button>
          
          <button 
            onClick={cancelSmsVerification}
            disabled={smsLoading}
            style={{ 
              width: '100%', 
              background: 'transparent', 
              color: '#6b7280', 
              border: 'none', 
              borderRadius: 6, 
              padding: '0.5rem', 
              fontSize: '0.9rem', 
              cursor: smsLoading ? 'not-allowed' : 'pointer',
              textDecoration: 'underline'
            }}
          >
            Cancel
          </button>
          
          {/* reCAPTCHA container */}
          <div id="recaptcha-container"></div>
        </div>
      </div>
    );
  }

  const sendingCode = loading && !isSignIn;
  const sendingLabel = "Sending your WhatsApp code. Please wait.";

  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: '12px', boxShadow: '0 4px 16px rgba(0,0,0,0.07)', padding: '2.5rem 2rem', minWidth: 320, position: 'relative' }}>
        {sendingCode && (
          <div style={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(255,255,255,0.92)',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1.5rem',
            textAlign: 'center',
            zIndex: 2,
          }}>
            <div>
              <div style={{ color: '#f97316', fontWeight: 'bold', fontSize: '1.15rem', marginBottom: '0.5rem' }}>
                Please wait
              </div>
              <div style={{ color: '#374151', lineHeight: 1.5 }}>{sendingLabel}</div>
            </div>
          </div>
        )}
        <h2 style={{ color: '#f97316', fontWeight: 'bold', textAlign: 'center', marginBottom: '1.5rem' }}>
          {isSignIn ? 'Sign In' : 'Create Account'}
        </h2>
        <form onSubmit={handleSubmit}>
                     {!isSignIn && (
             <>
               <input
                 type="text"
                 placeholder="Name"
                 value={name}
                 onChange={e => setName(e.target.value)}
                 style={{ width: '100%', padding: '0.75rem', marginBottom: '1rem', borderRadius: 6, border: '1px solid #e5e7eb', boxSizing: 'border-box' }}
                 autoComplete="name"
               />
               <input
                 type="text"
                 placeholder="Area"
                 value={area}
                 onChange={e => setArea(e.target.value)}
                 style={{ width: '100%', padding: '0.75rem', marginBottom: '1rem', borderRadius: 6, border: '1px solid #e5e7eb', boxSizing: 'border-box' }}
                 autoComplete="address-level2"
               />
             </>
           )}
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            style={{ width: '100%', padding: '0.75rem', marginBottom: '1rem', borderRadius: 6, border: '1px solid #e5e7eb', boxSizing: 'border-box' }}
            autoComplete="username"
          />
          {!isSignIn && (
            <input
              type="email"
              placeholder="Confirm Email"
              value={confirmEmail}
              onChange={e => setConfirmEmail(e.target.value)}
              style={{ width: '100%', padding: '0.75rem', marginBottom: '1rem', borderRadius: 6, border: '1px solid #e5e7eb', boxSizing: 'border-box' }}
              autoComplete="email"
            />
          )}
                    <div style={{ position: 'relative', marginBottom: '1rem' }}>
             <input
               type={showPassword ? "text" : "password"}
               placeholder="Password"
               value={password}
               onChange={e => setPassword(e.target.value)}
               style={{ width: '100%', padding: '0.75rem', paddingRight: '2.5rem', borderRadius: 6, border: '1px solid #e5e7eb', boxSizing: 'border-box' }}
               autoComplete={isSignIn ? "current-password" : "new-password"}
             />
             <button
               type="button"
               onClick={() => setShowPassword(!showPassword)}
               style={{
                 position: 'absolute',
                 right: '0.75rem',
                 top: '50%',
                 transform: 'translateY(-50%)',
                 background: 'none',
                 border: 'none',
                 cursor: 'pointer',
                 fontSize: '1.2rem',
                 color: '#9ca3af',
                 padding: '0.25rem',
                 display: 'flex',
                 alignItems: 'center',
                 justifyContent: 'center',
                 width: '24px',
                 height: '24px'
               }}
               title={showPassword ? "Hide password" : "Show password"}
             >
               {showPassword ? "🔒" : "👁️"}
             </button>
           </div>
                     {!isSignIn && (
             <div style={{ position: 'relative', marginBottom: '1rem' }}>
               <input
                 type={showConfirmPassword ? "text" : "password"}
                 placeholder="Confirm Password"
                 value={confirmPassword}
                 onChange={e => setConfirmPassword(e.target.value)}
                 style={{ width: '100%', padding: '0.75rem', paddingRight: '2.5rem', borderRadius: 6, border: '1px solid #e5e7eb', boxSizing: 'border-box' }}
                 autoComplete="new-password"
               />
               <button
                 type="button"
                 onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                 style={{
                   position: 'absolute',
                   right: '0.75rem',
                   top: '50%',
                   transform: 'translateY(-50%)',
                   background: 'none',
                   border: 'none',
                   cursor: 'pointer',
                   fontSize: '1.2rem',
                   color: '#9ca3af',
                   padding: '0.25rem',
                   display: 'flex',
                   alignItems: 'center',
                   justifyContent: 'center',
                   width: '24px',
                   height: '24px'
                 }}
                 title={showConfirmPassword ? "Hide password" : "Show password"}
               >
                 {showConfirmPassword ? "🔒" : "👁️"}
               </button>
             </div>
           )}
          {!isSignIn && (
            <>
              <div style={{ marginBottom: '0.5rem' }}>
                <div style={{ fontSize: '0.875rem', color: '#6b7280', fontStyle: 'italic' }}>
                  Will be used for verification so must be active
                </div>
              </div>
              <input
                type="text"
                placeholder="Seven digits - 7654321"
                value={contact}
                onChange={e => setContact(e.target.value.replace(/\D/g, '').slice(0, 7))}
                style={{ width: '100%', padding: '0.75rem', marginBottom: '1rem', borderRadius: 6, border: '1px solid #e5e7eb', boxSizing: 'border-box' }}
                autoComplete="tel"
              />
              <div style={{ marginBottom: '1rem' }}>
                <div style={{ fontSize: '0.875rem', color: '#374151', marginBottom: '0.5rem', fontWeight: 600 }}>
                  Mobile network
                </div>
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  {[
                    { value: 'bmobile', label: 'Bmobile' },
                    { value: 'digicel', label: 'Digicel' },
                  ].map((opt) => (
                    <label
                      key={opt.value}
                      style={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.4rem',
                        padding: '0.65rem 0.75rem',
                        borderRadius: 6,
                        border: carrier === opt.value ? '2px solid #f97316' : '1px solid #e5e7eb',
                        background: carrier === opt.value ? '#fff7ed' : '#fff',
                        cursor: 'pointer',
                        fontSize: '0.95rem',
                      }}
                    >
                      <input
                        type="radio"
                        name="carrier"
                        value={opt.value}
                        checked={carrier === opt.value}
                        onChange={() => handleCarrierChange(opt.value)}
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <div style={{ fontSize: '0.875rem', color: '#374151', marginBottom: '0.5rem', fontWeight: 600 }}>
                  Verification method
                </div>
                <div style={{
                  padding: '0.65rem 0.75rem',
                  borderRadius: 6,
                  border: '2px solid #f97316',
                  background: '#fff7ed',
                  fontSize: '0.95rem',
                  fontWeight: 600,
                }}>
                  WhatsApp
                </div>
                <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.4rem' }}>
                  Phone numbers are verified with WhatsApp.
                </div>
              </div>
            </>
          )}
          {isSignIn && (
            <div style={{ textAlign: 'right', marginBottom: '1rem' }}>
              <button type="button" onClick={handlePasswordReset} style={{ color: '#f97316', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', fontSize: '0.95rem' }}>
                Forgot password?
              </button>
            </div>
          )}
          {resetSent && (
            <div style={{ marginBottom: '1rem', textAlign: 'center' }}>
              <div style={{ color: 'green', marginBottom: '0.5rem' }}>Password reset email sent!</div>
              <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>Check spam or junk folder if not in inbox.</div>
            </div>
          )}
          {error && <div style={{ color: 'red', marginBottom: '1rem', textAlign: 'center' }}>{error}</div>}
          <button type="submit" style={{ width: '100%', background: isLockedOut() ? '#9ca3af' : '#f97316', color: '#fff', border: 'none', borderRadius: 6, padding: '0.75rem', fontWeight: 'bold', fontSize: '1rem', cursor: isLockedOut() ? 'not-allowed' : 'pointer', marginBottom: '1rem' }} disabled={loading || isLockedOut()}>
            {loading ? (isSignIn ? 'Signing In...' : 'Please wait...') : (isLockedOut() ? 'Account Locked' : (isSignIn ? 'Sign In' : 'Create Account'))}
          </button>
        </form>
        <button onClick={handleGoogleSignIn} style={{ width: '100%', background: '#fff', color: '#1f2937', border: '1px solid #f97316', borderRadius: 6, padding: '0.75rem', fontWeight: 'bold', fontSize: '1rem', cursor: 'pointer', marginBottom: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }} disabled={loading}>
          <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google" style={{ width: 22, height: 22 }} />
          Sign in with Google
        </button>
        <div style={{ textAlign: 'center' }}>
          {isSignIn ? (
            <span>Don't have an account? <button style={{ color: '#f97316', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }} onClick={() => { setIsSignIn(false); setError(""); setResetSent(false); setConfirmPassword(""); setContact(""); setCarrier(""); setVerifyChannel("whatsapp"); setShowPassword(false); setShowConfirmPassword(false); }}>Create Account</button></span>
          ) : (
            <span>Already have an account? <button style={{ color: '#f97316', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }} onClick={() => { setIsSignIn(true); setError(""); setResetSent(false); setConfirmPassword(""); setContact(""); setCarrier(""); setVerifyChannel("whatsapp"); setShowPassword(false); setShowConfirmPassword(false); }}>Sign In</button></span>
          )}
        </div>
        
        {/* reCAPTCHA container - invisible */}
        <div id="recaptcha-container"></div>
      </div>
    </div>
  );
}

export default Auth; 